import { type Node } from '@xyflow/react';
import { AllowedEmailTo, TaskScheduleUnitEnum } from 'automations/data/actionNodes';
import { z } from 'zod';

import { ActionTypes } from '../automationConfig';
import { CommonNodeDataSchema } from './node';

export const actionNodeDataSchema = CommonNodeDataSchema.extend({
  nodeSubType: z.nativeEnum(ActionTypes),
});

export const UpdateLeadNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.updateLead),
  additionalOptions: z.object({
    updateLead: z.array(z.object({
      schemaName: z.string({ required_error: 'Lead field is required' }),
      value: z.string({ required_error: 'Value is required' }).min(1, 'Value is required'),
    })).min(1, 'At least one lead field is required')
      .refine((fields) => new Set(fields.map(({ schemaName }) => schemaName)).size === fields.length, {
        message: 'All Lead Fields must be unique',
      }),
  }, {
    required_error: 'At least one lead field is required',
  }),
});

export const WebhookNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.webhook),
  additionalOptions: z.object({
    webhookId: z.union([z.string(), z.number()], {
      required_error: 'Webhook is required',
    }),
  }, {
    required_error: 'Webhook is required',
  }),
});

export const DistributeLeadNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.distributeLead),
  additionalOptions: z.object({
    leadDistributionId: z.number({ required_error: 'Lead Distribution Rule is required' }),
  }, {
    required_error: 'Lead Distribution Rule is required',
  }),
});

export const SendEmailNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.sendEmail),
  additionalOptions: z.object({
    sendEmail: z.object({
      to: z.nativeEnum(AllowedEmailTo, {
        required_error: 'Email To is required',
      }),
      from: z.string({
        required_error: 'Email From is required',
      }).min(1, 'Email From is required'),
      templateId: z.number({
        required_error: 'Email Template is required',
      }),
    }),
  }, {
    required_error: 'Email Content is required',
  }),
});

export const CreateTaskNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.createTask),
  additionalOptions: z.object({
    createTask: z.object({
      templateId: z.number({ required_error: 'Task Template is required' }),
      associateWith: z.literal('lead', { required_error: 'Associate With is required' }),
      owner: z.literal('lead.owner', { required_error: 'Owner is required' }),
      organizer: z.literal('lead.owner', { required_error: 'Organizer is required' }),
      subject: z.string().min(1, 'Subject is required'),
      description: z.string().min(1, 'Description is required'),

      schedule: z.object({
        value: z.number(),
        unit: z.nativeEnum(TaskScheduleUnitEnum),
        isAfter: z.boolean(),
        dependsOn: z.string(),
      }).required(),

      reminder: z.union([
        z.object({
          seconds: z.number(),
        }),
        z.object({
          beforeDays: z.number(),
          atTime: z.string(),
        }),
      ]).optional(),

      config: z.object({
        calendarInviteForLead: z.boolean().optional(),
        calendarInviteForOwner: z.boolean().optional(),
        calendarInviteForOrganizer: z.boolean().optional(),
      }).optional(),
    }),
  }, {
    required_error: 'Task Config is required',
  }),
});

export const CompleteTaskNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.completeTask),
  additionalOptions: z.object({
    completeTask: z.object({
      templateIds: z.array(z.number()).min(1, 'At least one template is required'),
    }),
  }, {
    required_error: 'Task template is required',
  }),
});

export const AddLeadActivityNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.addLeadActivity),
  additionalOptions: z.object({
    addLeadActivity: z.object({
      activityId: z.number({ required_error: 'Activity is required' }),
      activityAttributes: z.array(z.object({
        schemaName: z.string(),
        value: z.string(),
      })).optional(),
    }),
  }, {
    required_error: 'Activity is required',
  }),
});

export type ActionNodeData = z.infer<typeof actionNodeDataSchema>;
export type UpdateLeadNodeData = z.infer<typeof UpdateLeadNodeDataSchema>;
export type WebhookNodeData = z.infer<typeof WebhookNodeDataSchema>;
export type DistributeLeadNodeData = z.infer<typeof DistributeLeadNodeDataSchema>;
export type SendEmailNodeData = z.infer<typeof SendEmailNodeDataSchema>;
export type CreateTaskNodeData = z.infer<typeof CreateTaskNodeDataSchema>;
export type AddLeadActivityNodeData = z.infer<typeof AddLeadActivityNodeDataSchema>;
export type CompleteTaskNodeData = z.infer<typeof CompleteTaskNodeDataSchema>;

export type ActionNodeType = Node<ActionNodeData>;
export type UpdateLeadNodeType = Node<UpdateLeadNodeData>;
export type WebhookNodeType = Node<WebhookNodeData>;
export type DistributeLeadNodeType = Node<DistributeLeadNodeData>;
export type SendEmailNodeType = Node<SendEmailNodeData>;
export type CreateTaskNodeType = Node<CreateTaskNodeData>;
export type CompleteTaskNodeType = Node<CompleteTaskNodeData>;
export type AddLeadActivityNodeType = Node<AddLeadActivityNodeData>;
